import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import { InfoCircle } from "iconsax-react";
import {
  PlatformSyscodeString,
  PLATFORM_SYSCODE,
} from "../../../constants/platform_syscode.jsx";
import generateHmacHeaders from "./components/generate_hmac_form.jsx";

const errorIcon = (
  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
    <InfoCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
  </div>
);

const HmacSimulatorLayout = () => {
  const [isDisabled, setIsDisabled] = useState(false);
  const [optionLoad, setOptionLoad] = useState(true);
  const [requestUrl, setRequestUrl] = useState();
  const [load, setLoad] = useState(false);
  const [responseHeaders, setResponseHeaders] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  const {
    register,
    setError,
    formState: { errors },
    handleSubmit,
    control,
  } = useForm({});

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const requestMethodOptions = [
    { value: "GET", label: "GET" },
    { value: "POST", label: "POST" },
    { value: "PUT", label: "PUT" },
    { value: "DELETE", label: "DELETE" },
  ];

  function formatData(data) {
    var newData = data;
    const jsonInput = newData.requestBody;
    const requestMethod = newData.requestMethod.value;
    const developerPlatformSyscode = newData.developerPlatformSyscode;

    try {
      // Try parsing the JSON input
      const parsedJson = JSON.parse(jsonInput);
      if (
        typeof parsedJson !== "object" ||
        Array.isArray(parsedJson) ||
        parsedJson === null
      ) {
        throw new Error("The JSON must be an object (e.g., {})."); // Trigger the catch block
      }

      newData.requestBody = parsedJson;
    } catch (error) {
      // If parsing fails, set an error message or show feedback to the user
      console.error("Invalid JSON format:", error);
      setIsDisabled(false);
      setError("requestBody", {
        type: "manual",
        message: "Invalid JSON format.",
      });
      return null;
    }
    if (newData.isoDatetime) {
      const check = isValidIsoDateTime(newData.isoDatetime);
      if (!check) {
        setIsDisabled(false);
        setError("isoDatetime", {
          type: "manual",
          message: "Invalid datetime format.",
        });
        return null;
      }
    }
    const parsedUrl = new URL(data.requestUrl);
    const requestHost = parsedUrl.host;
    const requestUrlPathQuery = `${parsedUrl.pathname}${parsedUrl.search}`;

    newData.requestHost = requestHost;
    newData.requestUrlPathQuery = requestUrlPathQuery;
    newData.requestMethod = requestMethod;
    newData.developerPlatformSyscode = developerPlatformSyscode;
    return newData;
  }

  function isValidIsoDateTime(dateTimeString) {
    const date = new Date(dateTimeString);
    return date.toISOString() === dateTimeString;
  }

  const onSubmit = async (data) => {
    setIsDisabled(true); // Disable the submit button

    const newData = formatData(data);

    if (newData) {
      const headers = generateHmacHeaders(
        newData.requestHost,
        newData.requestMethod,
        newData.requestUrlPathQuery,
        newData.requestBody,
        newData.developerPlatformSyscode,
        newData.developerApiKey,
        newData.developerProjectId,
        newData.developerSecretKey,
        newData.isoDatetime
      );
      setResponseHeaders(headers);
      setIsDisabled(false);
    }
  };

  if (!load) {
    return (
      <div className="w-full bg-gray-100 min-h-screen">
        <div className={`p-4 ${isMobile ? 'space-y-4' : 'mt-4'}`}>
          
          {/* Content Section */}
          <div className="w-full flex justify-center">
            <div
              className="bg-white border-white border rounded-2xl p-4"
              style={{
                width: isMobile ? "100%" : "70%",
                maxWidth: isMobile ? "none" : "800px",
                margin: isMobile ? "0" : "20px",
                padding: isMobile ? "16px" : "24px"
              }}
            >
              <div className="mt-4 mb-8 text-center">
                {/* Responsive Header Text */}
                {isMobile ? (
                  <h1
                    className="text-2xl font-semibold text-black"
                    style={{ 
                      fontSize: "18px",
                      lineHeight: "1.4",
                      padding: "0 5px"
                    }}
                  >
                    HMAC Simulator for Developer Project API Headers
                    <br />
                    <span style={{ fontSize: "16px", fontWeight: "normal", color: "#666" }}>
                      Verify HTTP POST | GET | PUT | DELETE requests
                    </span>
                  </h1>
                ) : (
                  <h1
                    className="text-2xl font-semibold text-black"
                    style={{ 
                      fontSize: "25px",
                      lineHeight: "1.2"
                    }}
                  >
                    This simulator may be used to verify developer project API HTTP
                    POST|GET|PUT|DELETE Headers
                  </h1>
                )}
              </div>
              
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mt-2">
                  <div className="mt-8 sm:col-span-2" style={{ color: "black" }}>
                    <label
                      htmlFor="requestMethod"
                      className="block text-sm font-medium text-gray-600 mt-4"
                      style={{ 
                        textAlign: "left",
                        fontSize: isMobile ? "14px" : "14px"
                      }}
                    >
                      Request Method
                    </label>
                    <Controller
                      name="requestMethod"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Select
                          isClearable
                          {...field}
                          options={requestMethodOptions}
                          isSearchable={true}
                          isMulti={false}
                          placeholder="Select Request Method"
                          className="text-sm"
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: isMobile ? "44px" : "38px",
                              fontSize: isMobile ? "16px" : "14px"
                            }),
                            menu: (base) => ({
                              ...base,
                              fontSize: isMobile ? "16px" : "14px"
                            })
                          }}
                        />
                      )}
                    />
                    <div className="mt-2">
                      <label
                        htmlFor="requestUrl"
                        className="block text-sm font-medium text-gray-600"
                        style={{ 
                          textAlign: "left",
                          fontSize: isMobile ? "14px" : "14px"
                        }}
                      >
                        Request URL
                      </label>
                      <div
                        className="relative rounded-md shadow-sm"
                        style={{ color: "black" }}
                      >
                        <input
                          type="text"
                          name="requestUrl"
                          id="requestUrl"
                          className={
                            !errors.requestUrl
                              ? "block w-full pr-10 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
                              : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                          }
                          placeholder="Enter Request URL"
                          {...register("requestUrl", { required: true })}
                          value={requestUrl}
                          onChange={(e) => setRequestUrl(e.target.value)}
                          style={{
                            minHeight: isMobile ? "44px" : "38px",
                            fontSize: isMobile ? "16px" : "14px"
                          }}
                        />
                        {errors.requestUrl?.type === "required" && errorIcon}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <label
                    htmlFor="requestBody"
                    className="block text-sm font-medium text-gray-600 mt-4"
                    style={{
                      textAlign: "left",
                      marginBottom: "5px",
                      marginTop: "-10px",
                      fontSize: isMobile ? "14px" : "14px"
                    }}
                  >
                    JSON Request Body
                  </label>
                  <div className="relative rounded-md shadow-sm text-black">
                    <textarea
                      name="requestBody"
                      id="requestBody"
                      rows={isMobile ? "6" : "8"}
                      className={
                        !errors.requestBody
                          ? "block w-full pr-10 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
                          : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                      }
                      placeholder="Enter JSON Request Body"
                      {...register("requestBody", {
                        required: true,
                      })}
                      style={{
                        fontFamily: "monospace",
                        resize: "vertical",
                        fontSize: isMobile ? "14px" : "14px"
                      }}
                    ></textarea>
                    {errors.requestBody?.type === "required" && errorIcon}
                  </div>
                  {errors.requestBody?.message && (
                    <>
                      <div>
                        <p className="block text-sm text-red-600">
                          {errors.requestBody.message}
                        </p>
                      </div>
                    </>
                  )}
                </div>
                <div className="mt-2">
                  <label
                    htmlFor="developerProjectId"
                    className="block text-sm font-medium text-gray-600 mt-4"
                    style={{
                      textAlign: "left",
                      marginBottom: "5px",
                      marginTop: "-10px",
                      fontSize: isMobile ? "14px" : "14px"
                    }}
                  >
                    Developer Project ID
                  </label>
                  <div className="relative rounded-md shadow-sm mb-2 text-black">
                    <input
                      type="text"
                      name="developerProjectId"
                      id="developerProjectId"
                      className={
                        !errors.developerProjectId
                          ? "block w-full pr-10 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
                          : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                      }
                      placeholder="Enter Developer Project ID"
                      {...register("developerProjectId", {
                        required: true,
                      })}
                      style={{ 
                        marginBottom: "20px",
                        minHeight: isMobile ? "44px" : "38px",
                        fontSize: isMobile ? "16px" : "14px"
                      }}
                    />
                    {errors.developerProjectId?.type === "required" && errorIcon}
                  </div>
                </div>
                <div className="mt-2">
                  <label
                    htmlFor="developerApiKey"
                    className="block text-sm font-medium text-gray-600 mt-4"
                    style={{
                      textAlign: "left",
                      marginBottom: "5px",
                      marginTop: "-10px",
                      fontSize: isMobile ? "14px" : "14px"
                    }}
                  >
                    Developer API Key
                  </label>
                  <div className="relative rounded-md shadow-sm mb-2 text-black">
                    <input
                      type="text"
                      name="developerApiKey"
                      id="developerApiKey"
                      className={
                        !errors.developerApiKey
                          ? "block w-full pr-10 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
                          : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                      }
                      placeholder="Enter Developer API Key"
                      {...register("developerApiKey", {
                        required: true,
                      })}
                      style={{ 
                        marginBottom: "20px",
                        minHeight: isMobile ? "44px" : "38px",
                        fontSize: isMobile ? "16px" : "14px"
                      }}
                    />
                    {errors.developerApiKey?.type === "required" && errorIcon}
                  </div>
                </div>
                <div className="mt-2">
                  <label
                    htmlFor="developerSecretKey"
                    className="block text-sm font-medium text-gray-600 mt-4"
                    style={{
                      textAlign: "left",
                      marginBottom: "5px",
                      marginTop: "-10px",
                      fontSize: isMobile ? "14px" : "14px"
                    }}
                  >
                    Developer Project Secret Key
                  </label>
                  <div className="relative rounded-md shadow-sm mb-2 text-black">
                    <input
                      type="text"
                      name="developerSecretKey"
                      id="developerSecretKey"
                      className={
                        !errors.developerSecretKey
                          ? "block w-full pr-10 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
                          : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                      }
                      placeholder="Enter Developer Project Secret Key"
                      {...register("developerSecretKey", {
                        required: true,
                      })}
                      style={{ 
                        marginBottom: "20px",
                        minHeight: isMobile ? "44px" : "38px",
                        fontSize: isMobile ? "16px" : "14px"
                      }}
                    />
                    {errors.developerSecretKey?.type === "required" && errorIcon}
                  </div>
                </div>
                <div className="mt-2">
                  <label
                    htmlFor="developerPlatformSyscode"
                    className="block text-sm font-medium text-gray-600 mt-4"
                    style={{ 
                      textAlign: "left", 
                      marginBottom: "5px",
                      fontSize: isMobile ? "14px" : "14px"
                    }}
                  >
                    Platform Syscode
                  </label>
                  <div className="relative rounded-md shadow-sm mb-2 text-black">
                    <input
                      type="text"
                      name="developerPlatformSyscode"
                      id="developerPlatformSyscode"
                      className={
                        !errors.developerPlatformSyscode
                          ? "block w-full pr-10 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
                          : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                      }
                      placeholder="Enter Developer Project Platform Syscode"
                      {...register("developerPlatformSyscode", {
                        required: true,
                      })}
                      style={{ 
                        marginBottom: "20px",
                        minHeight: isMobile ? "44px" : "38px",
                        fontSize: isMobile ? "16px" : "14px"
                      }}
                    />
                    {errors.developerSecretKey?.type === "required" && errorIcon}
                  </div>
                </div>
                <div className="mt-2">
                  <label
                    htmlFor="isoDatetime"
                    className="block text-sm font-medium text-gray-600 mt-4"
                    style={{
                      textAlign: "left",
                      marginBottom: "5px",
                      marginTop: "-10px",
                      fontSize: isMobile ? "14px" : "14px"
                    }}
                  >
                    ISO Datetime (Eg. 2024-11-08T08:00:00.000Z)
                  </label>
                  <div className="relative rounded-md shadow-sm mb-2 text-black">
                    <input
                      type="text"
                      name="isoDatetime"
                      id="isoDatetime"
                      className={
                        !errors.isoDatetime
                          ? "block w-full pr-10 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
                          : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                      }
                      placeholder="Enter ISO Datetime manually. Else, leave blank for simulator to auto generate."
                      {...register("isoDatetime")}
                      style={{
                        minHeight: isMobile ? "44px" : "38px",
                        fontSize: isMobile ? "16px" : "14px"
                      }}
                    />
                    {errors.isoDatetime?.type === "required" && errorIcon}
                  </div>
                  {errors.isoDatetime?.message && (
                    <>
                      <div>
                        <p className="block text-sm text-red-600">
                          {errors.isoDatetime.message}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* create button */}
                <div>
                  <button
                    type="submit"
                    className="hover:bg-blue-500 text-blue-500 hover:text-white outline outline-blue-500 font-bold py-2 px-12 rounded-full mt-10"
                    disabled={isDisabled}
                    style={{
                      fontSize: isMobile ? "16px" : "16px",
                      padding: isMobile ? "12px 24px" : "8px 48px",
                      minHeight: isMobile ? "44px" : "auto",
                      width: isMobile ? "100%" : "auto"
                    }}
                  >
                    Generate
                  </button>
                </div>
                
                {responseHeaders.length > 0 && (
                  <div className="mt-4">
                    <h3
                      className="text-lg font-bold text-gray-800"
                      style={{ 
                        fontSize: isMobile ? "20px" : "27px",
                        textAlign: isMobile ? "center" : "left",
                        marginBottom: isMobile ? "15px" : "10px"
                      }}
                    >
                      HMAC-SHA256 Headers:
                    </h3>
                    <ul className="mt-2 space-y-4 text-left">
                      {responseHeaders.map((header, index) => (
                        <li key={index} style={{ 
                          padding: isMobile ? "12px" : "0",
                          borderBottom: isMobile ? "1px solid #e5e7eb" : "none",
                          marginBottom: isMobile ? "15px" : "0",
                          backgroundColor: isMobile ? "#f8f9fa" : "transparent",
                          borderRadius: isMobile ? "8px" : "0"
                        }}>
                          {/* Header title */}
                          <div
                            className="text-md font-semibold text-gray-900"
                            style={{ 
                              fontSize: isMobile ? "15px" : "20px",
                              marginBottom: isMobile ? "8px" : "4px",
                              wordBreak: "break-word"
                            }}
                          >
                            {header.header}
                          </div>
                          {/* Header value */}
                          <div
                            className="text-sm text-gray-700"
                            style={{ 
                              fontSize: isMobile ? "12px" : "15px",
                              wordBreak: "break-all",
                              fontFamily: "monospace",
                              backgroundColor: isMobile ? "#ffffff" : "transparent",
                              padding: isMobile ? "8px" : "0",
                              borderRadius: isMobile ? "4px" : "0",
                              border: isMobile ? "1px solid #e5e7eb" : "none",
                              lineHeight: isMobile ? "1.4" : "1.2"
                            }}
                          >
                            {header.value}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mobile-friendly loading state
  return (
    <div className="w-full bg-gray-100 min-h-screen">
      <div className="flex items-center justify-center" style={{ minHeight: "50vh" }}>
        <div className="text-center p-8">
          <div className={`animate-spin rounded-full border-4 border-blue-200 border-t-blue-600 mx-auto mb-4 ${
            isMobile ? 'h-12 w-12' : 'h-16 w-16'
          }`}></div>
          <h3 className={`font-medium text-gray-900 ${isMobile ? 'text-lg' : 'text-xl'}`}>
            Loading HMAC Simulator
          </h3>
          <p className={`text-gray-600 ${isMobile ? 'text-sm' : 'text-base'}`}>
            Please wait while we prepare the simulator...
          </p>
        </div>
      </div>
    </div>
  );
};

export default HmacSimulatorLayout;